import React, {
  useState, useEffect, useRef, useCallback,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  IconButton,
  Typography,
  CircularProgress,
  Tooltip,
  Drawer,
  useTheme,
  useMediaQuery,
  Badge,
  Fab,
  SpeedDial,
  SpeedDialIcon,
  Popover,
  Grid,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Button,
  Alert,
  Chip,
  Card,
  CardContent,
} from '@mui/material';
import {
  Add as AddIcon,
  History as HistoryIcon,
  Clear as ClearIcon,
  Settings as SettingsIcon,
  ChevronLeft as ChevronLeftIcon,
  Notifications,
  Insights,
  EmojiEmotions,
  Stop as StopIcon,
} from '@mui/icons-material';

// Import hooks
import { toast } from 'react-toastify';
import api from '@/utils/api';
import { useConversations } from '../hooks/useConversations.jsx';
import { useUserSettings } from '../hooks/useUserSettings';
import { useRichChat } from '../hooks/useRichChat';
import { chatCompletion } from '../api/chat.api';
import {
  executeFlowStreaming,
  executeFlowEnhanced,
  recoverFlowExecution,
  executeAgenticFlow,
  submitFormStreaming,
} from '../api/agenticFlow.api';
import { getLangGraphBackendUrl, getLangGraphFlowStatusUrl, getLangGraphSubmitFormUrl } from '../utils/langgraphConfig.js';
import mcpService from '../services/mcpService';
import axiosClient from '../api/axios.client';
import {
  ErrorRecoveryModal,
  InlineErrorRecovery,
} from '../components/ErrorRecovery';

// Import components
import ChatMessages from '../components/ChatMessages';
import ChatInputSection from '../components/ChatInputSection';
import FormOptionsDrawer from '../components/FormOptionsDrawer';
import ConversationList from '../components/ConversationList';
import RichChatInterface from '../components/RichChatInterface';

// Import new widgets
import ChatStatusBar from '../components/ChatWidgets/ChatStatusBar';
import ChatQuickActions from '../components/ChatWidgets/ChatQuickActions';
import ChatSidePanel from '../components/ChatWidgets/ChatSidePanel';
import ChatNotificationCenter from '../components/ChatWidgets/ChatNotificationCenter';

const messageType = {
  answer: 'answer',
  question: 'question',
  form: 'form',
  system: 'system',
};

function HomePageEnhanced({ darkMode: initialDarkMode }) {
  const username = localStorage.getItem('username')
    || JSON.parse(localStorage.getItem('user') || '{}').email
    || JSON.parse(localStorage.getItem('user') || '{}').username;

  const navigate = useNavigate();
  const inputRef = useRef();
  const messagesEndRef = useRef();
  let theme; let
    isMobile;

  try {
    theme = useTheme();
    isMobile = useMediaQuery ? useMediaQuery(theme.breakpoints.down('md')) : false;
    if (!theme.transitions) {
      theme.transitions = {
        create: () => '',
        easing: {},
        duration: {},
      };
    }
  } catch (e) {
    theme = {
      transitions: { create: () => '', easing: {}, duration: {} },
      breakpoints: { down: () => false },
    };
    isMobile = false;
  }

  // Enhanced state declarations
  const [onRequest, setOnRequest] = useState(false);
  const [question, setQuestion] = useState('');
  const [file, setFile] = useState(null);
  const [isListening, setIsListening] = useState(false);
  const [typingInstance, setTypingInstance] = useState(null);
  const [typingPaused, setTypingPaused] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [showConversations, setShowConversations] = useState(false);
  const [formOptionsOpen, setFormOptionsOpen] = useState(false);
  const [mobileForceRender, setMobileForceRender] = useState(0);
  const [sessionStartTime, setSessionStartTime] = useState(Date.now());
  const [localIsProcessing, setLocalIsProcessing] = useState(false);

  // Flow state management - Enhanced for streaming
  const [activeFlowState, setActiveFlowState] = useState({
    isActive: false,
    flowId: null,
    executionId: null,
    sessionId: null,
    context: null,
    // NEW: Streaming-specific state
    streamingConnection: null,
    progress: 0,
    currentNode: null,
    nodeStatuses: {},
    executionStartTime: null,
    realTimeUpdates: [],
    waitingForForm: false,
    completed: false,
    startTime: null,
    elapsedSeconds: 0,
    currentFormNodeId: null,
  });

  // Error recovery state
  const [errorRecoveryState, setErrorRecoveryState] = useState({
    showModal: false,
    error: null,
    executionId: null,
    executionData: null,
    lastError: null,
  });

  // Monitoring dashboard state
  const [monitoringMode, setMonitoringMode] = useState(false);
  const [performanceMetrics, setPerformanceMetrics] = useState(null);
  const [systemStatus, setSystemStatus] = useState(null);
  const [debugData, setDebugData] = useState(null);
  const [monitoringDialogOpen, setMonitoringDialogOpen] = useState(false);

  // Performance optimization state
  const [performanceMode, setPerformanceMode] = useState(false);
  const [enableCaching, setEnableCaching] = useState(true);
  const [enableCompression, setEnableCompression] = useState(false);
  const [optimizeMemoryUsage, setOptimizeMemoryUsage] = useState(true);
  const [cacheTTL, setCacheTTL] = useState(3600); // 1 hour
  const [maxConcurrentRequests, setMaxConcurrentRequests] = useState(5);

  // Enhanced timer management for active flows
  // Timer state is handled in the useEffect with setInterval
  const [flowStartTime, setFlowStartTime] = useState(null);

  // Timer effect for active flows - runs continuously when flow is active
  useEffect(() => {
    let timerInterval = null;

    if (activeFlowState.isActive && activeFlowState.startTime) {
      // Start timer that updates every second, but pause when waiting for form input
      timerInterval = setInterval(() => {
        setActiveFlowState((prev) => {
          // Only update timer if not waiting for form input
          if (!prev.waitingForForm) {
            const elapsedSeconds = Math.floor((Date.now() - prev.startTime) / 1000);
            return {
              ...prev,
              elapsedSeconds,
            };
          }
          return prev; // Keep current state
        });
      }, 1000);
    }

    // Cleanup timer on unmount or when flow becomes inactive
    return () => {
      if (timerInterval) {
        clearInterval(timerInterval);
      }
    };
  }, [activeFlowState.isActive, activeFlowState.startTime, activeFlowState.waitingForForm]);

  // New widget states
  const [showSidePanel, setShowSidePanel] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [isConnected, setIsConnected] = useState(true);
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [insights, setInsights] = useState([]);

  // Emoji picker state
  const [emojiAnchorEl, setEmojiAnchorEl] = useState(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  // Common emojis for quick access
  const commonEmojis = [
    '😀', '😃', '😄', '😁', '😆', '😅', '😂', '🤣', '😊', '😇',
    '🙂', '🙃', '😉', '😌', '😍', '🥰', '😘', '😗', '😙', '😚',
    '😋', '😛', '😝', '😜', '🤪', '🤨', '🧐', '🤓', '😎', '🤩',
    '🥳', '😏', '😒', '😞', '😔', '😟', '😕', '🙁', '☹️', '😣',
    '😖', '😫', '😩', '🥺', '😢', '😭', '😤', '😠', '😡', '🤬',
    '🤯', '😳', '🥵', '🥶', '😱', '😨', '😰', '😥', '😓', '🤗',
    '🤔', '🤭', '🤫', '🤥', '😶', '😐', '😑', '😯', '😦', '😧',
    '😮', '😲', '🥱', '😴', '🤤', '😪', '😵', '🤐', '🥴', '🤢',
    '🤮', '🤧', '😷', '🤒', '🤕', '🤑', '🤠', '💩', '👻', '💀',
    '☠️', '👽', '👾', '🤖', '😺', '😸', '😹', '😻', '😼', '😽',
    '🙀', '😿', '😾', '🙈', '🙉', '🙊', '💌', '💘', '💝', '💖',
    '💗', '💓', '💞', '💕', '💟', '❣️', '💔', '❤️', '🧡', '💛',
    '💚', '💙', '💜', '🖤', '🤍', '🤎', '💯', '💢', '💥', '💫',
    '💦', '💨', '🕳️', '💬', '🗨️', '🗯️', '💭', '💤', '👋', '🤚',
    '🖐️', '✋', '🖖', '👌', '🤌', '🤏', '✌️', '🤞', '🤟', '🤘',
    '🤙', '👈', '👉', '👆', '🖕', '👇', '☝️', '👍', '👎', '✊',
    '👊', '🤛', '🤜', '👏', '🙌', '👐', '🤲', '🤝', '🙏', '✍️',
    '💪', '🦾', '🦿', '🦵', '🦶', '👂', '🦻', '👃', '🧠', '🫀',
    '🫁', '🦷', '🦴', '👀', '👁️', '👅', '👄', '💋', '🩸', '🩸',
    '🩸', '🩸', '🩸', '🩸', '🩸', '🩸', '🩸', '🩸', '🩸', '🩸',
  ];

  const recognition = useRef(null);
  const speech = useRef(null);

  // Manual scroll to bottom function
  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current) {
      // Use scrollIntoView directly - it's more reliable for this use case
      messagesEndRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      });
    }
  }, []);

  // Conversation management
  const {
    conversations,
    currentConversationId,
    currentMessages,
    createNewConversation,
    selectConversation,
    deleteConversation,
    renameConversation,
    toggleFavorite,
    archiveConversation,
    addMessageToConversation,
    clearCurrentConversation,
  } = useConversations(username);

  // User settings
  const {
    settings,
    loading: settingsLoading,
    updateChatSettings,
    updateUISettings,
  } = useUserSettings();

  // Model and flow states
  const [selectedModel, setSelectedModel] = useState('llama4');
  const [selectedFlow, setSelectedFlow] = useState(null);
  const [flowExecutionId, setFlowExecutionId] = useState(null);
  const [flowSessionId, setFlowSessionId] = useState(null);
  const [flowContext, setFlowContext] = useState({});

  // MCP states
  const [selectedMCPServer, setSelectedMCPServer] = useState(null);
  const [selectedMCPTool, setSelectedMCPTool] = useState(null);

  const [temperature, setTemperature] = useState(0.7);
  const [playAudio, setPlayAudio] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    if (initialDarkMode !== undefined) return initialDarkMode;
    const saved = localStorage.getItem('theme');
    return saved === 'dark';
  });

  // Rich chat handlers
  const handleSendMessage = async (message) => {
    let conversationId = currentConversationId;
    if (!conversationId) {
      const newConversation = await createNewConversation();
      conversationId = newConversation.id;
    }

    // Update session start time for new messages
    setSessionStartTime(Date.now());

    // Remove duplicate addMessageToConversation call since getAnswerWithContent handles it
    if (isMobile) setMobileForceRender((prev) => prev + 1);

    // Call getAnswer with the message content directly and pass the conversationId
    await getAnswerWithContent(message.content, conversationId);
  };

  const handleFormSubmission = async (formId, data) => {
    console.log('🔍 handleFormSubmission called with:', { formId, data, dataType: typeof data });

    // Use execution ID from streaming state or fallback to legacy flowExecutionId
    const currentExecutionId = activeFlowState.executionId || flowExecutionId;

    console.log('🔍 DEBUG - Form submission parameters:', {
      formId,
      data,
      activeFlowState_currentFormNodeId: activeFlowState.currentFormNodeId,
      activeFlowState_executionId: activeFlowState.executionId,
      flowExecutionId,
      currentExecutionId,
    });
    setLocalIsProcessing(true);

    try {
      if (!currentExecutionId) {
        console.error('No active flow execution ID found');
        toast.error('No active flow execution found');
        return;
      }

      // Get the LangGraph backend URL
      const langgraphBackendUrl = getLangGraphBackendUrl();

      // Submit form data to continue the paused flow
      const formDataToSubmit = {
        ...data,
        form_node_id: activeFlowState.currentFormNodeId,
      };

      console.log('🔍 DEBUG - Form submission details:', {
        activeFlowState_currentFormNodeId: activeFlowState.currentFormNodeId,
        data_form_node_id: data.form_node_id,
        final_form_node_id: formDataToSubmit.form_node_id,
        activeFlowState,
      });

      console.log('🔍 formDataToSubmit:', formDataToSubmit);
      console.log('🔍 Starting streaming form submission...');
      toast.success('Form submitted! Continuing execution...');

      // Use streaming form submission instead of REST
      await submitFormStreaming(
        currentExecutionId,
        formDataToSubmit,
        // onUpdate callback
        (update) => {
          console.log('📡 Form streaming update:', update.type, update.node_id);

          // Handle different update types
          switch (update.type) {
            case 'node_complete':
              console.log(`🚀 Node completed: ${update.node_id}`);
              break;

            case 'form_submitted':
              console.log('✅ Form submitted successfully:', update.message);
              // Continue execution - no UI action needed
              break;

            case 'execution_progress':
              console.log('⏳ Background processing:', update.message);
              // Show progress in UI
              setActiveFlowState((prev) => ({
                ...prev,
                isActive: true,
                isExecuting: true,
                waitingForForm: false,
                progress: update.message || 'Processing...',
              }));
              break;

            case 'execution_timeout':
              console.warn('⏰ Background processing timeout:', update.message);
              // Show timeout message but keep flow active
              setActiveFlowState((prev) => ({
                ...prev,
                progress: update.message || 'Processing is taking longer than expected...',
              }));
              break;

            case 'form_pause':
              console.log('📝 Another form pause detected:', update.form_node_waiting);
              handleStreamingFormPause(update, currentConversationId);
              break;

            case 'flow_complete':
              console.log('✅ Flow completed via form streaming');
              handleStreamingFlowComplete(update, currentConversationId);
              break;

            case 'flow_progress':
              console.log('🔄 Flow continuing:', update.message);
              // Update progress if available
              if (update.data) {
                setActiveFlowState((prev) => ({
                  ...prev,
                  isActive: true,
                  isExecuting: true,
                  waitingForForm: false,
                }));
              }
              break;

            case 'flow_error':
            case 'form_error':
              console.error('❌ Flow/Form error via streaming:', update.error);
              handleStreamingFlowError(update, currentConversationId);
              break;
          }
        },
        // onError callback
        (error) => {
          console.error('❌ Form streaming error:', error);
          toast.error('Form submission failed. Please try again.');
        },
      );

      // Return early - streaming handles the rest
      return;
      // OLD CODE REMOVED (was unreachable) - see git history if needed
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('Error submitting form. Please try again.');
    } finally {
      setLocalIsProcessing(false);
    }
  };

  // NEW: Real-time streaming for flow execution
  const streamFlowExecution = useCallback(async (flowData, input, conversationId) => {
    try {
      console.log('🔄 Starting streaming execution for:', flowData.name);

      // Initialize timer for active flow
      const streamStartTime = Date.now();
      setFlowStartTime(streamStartTime);

      // Update flow state to indicate streaming start with timer
      setActiveFlowState((prev) => ({
        ...prev,
        isActive: true,
        isExecuting: true,
        executionStartTime: streamStartTime,
        startTime: streamStartTime,
        elapsedSeconds: 0,
        progress: 0,
        currentNode: null,
        nodeStatuses: {},
        realTimeUpdates: [],
        waitingForForm: false,
        isPaused: false,
      }));

      const streamConnection = await executeFlowStreaming(
        flowData,
        input,
        // onUpdate callback - handles real-time updates
        (update) => {
          console.log('📡 Streaming update received:', update.type, update.node_id);

          setActiveFlowState((prev) => ({
            ...prev,
            progress: update.progress?.percentage || prev.progress,
            currentNode: update.node_id || prev.currentNode,
            realTimeUpdates: [...prev.realTimeUpdates, update],
            nodeStatuses: {
              ...prev.nodeStatuses,
              [update.node_id]: {
                status: update.type === 'node_complete' ? 'completed'
                  : update.type === 'node_error' ? 'error' : 'running',
                timestamp: update.timestamp,
                executionTime: update.execution_time,
              },
            },
          }));

          // Handle different update types
          switch (update.type) {
            case 'node_complete':
              console.log(`✅ Node completed: ${update.node_id}`);
              break;

            case 'flow_complete':
              console.log('🎉 Flow completed successfully');
              handleStreamingFlowComplete(update, conversationId);
              break;

            case 'flow_error':
              console.error('❌ Flow error:', update.error);
              handleStreamingFlowError(update, conversationId);
              break;

            case 'node_start':
              console.log(`🚀 Node started: ${update.node_id}`);
              break;

            case 'form_pause':
              console.log('📝 Form pause detected:', update.form_node_waiting);
              handleStreamingFormPause(update, conversationId);
              break;
          }
        },
        // onError callback
        (error) => {
          console.error('❌ Streaming error:', error);
          toast.error('Streaming connection failed. Falling back to polling...');

          // Fallback to traditional polling if streaming fails
          fallbackToPolling(flowData, input, conversationId);
        },
      );

      // Store the streaming connection for cleanup
      setActiveFlowState((prev) => ({
        ...prev,
        streamingConnection: streamConnection,
      }));

      return streamConnection;
    } catch (error) {
      console.error('Failed to start streaming:', error);
      toast.error('Failed to start real-time execution. Using fallback method...');

      // Fallback to traditional execution
      return fallbackToPolling(flowData, input, conversationId);
    }
  }, []);

  // Handle successful streaming flow completion
  const handleStreamingFlowComplete = useCallback((update, conversationId) => {
    const answerMessage = {
      type: messageType.answer,
      content: update.data?.result || update.data?.ai_response || 'Flow completed successfully',
      timestamp: new Date(),
      id: `flow_complete_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      metadata: {
        executionTime: update.execution_time,
        totalNodes: update.progress?.total,
        completedNodes: update.progress?.current,
        streaming: true,
      },
    };

    addMessageToConversation(conversationId, answerMessage);
    if (isMobile) setTimeout(() => setMobileForceRender((prev) => prev + 1), 100);

    // Reset flow state and stop timer
    setActiveFlowState((prev) => ({
      ...prev,
      isActive: false,
      isExecuting: false,
      completed: true,
      waitingForForm: false,
      streamingConnection: null,
      startTime: null,
      elapsedSeconds: 0,
    }));

    setOnRequest(false);
    setLocalIsProcessing(false);
  }, [addMessageToConversation, isMobile, setOnRequest]);

  // Handle streaming form pause
  const handleStreamingFormPause = useCallback((update, conversationId) => {
    console.log('🔍 Handling streaming form pause:', update);
    console.log('🔍 DEBUG - Current activeFlowState before form pause:', {
      executionId: activeFlowState.executionId,
      flowId: activeFlowState.flowId,
      isActive: activeFlowState.isActive,
    });
    console.log('🔍 DEBUG - Update execution_id:', update.execution_id);

    // Extract form data from the update
    const formFields = update.form_fields || [];
    const formNodeId = update.form_node_waiting;
    const formTitle = update.form_title || 'Form Input Required';
    const formDescription = update.form_description || 'Please fill out the form below.';
    const aiResponse = update.ai_response || '';

    if (formFields && Array.isArray(formFields) && formFields.length > 0 && formNodeId) {
      // First, add AI response as a separate message if it exists
      if (aiResponse.trim()) {
        const aiMessage = {
          type: messageType.answer,
          content: aiResponse,
          timestamp: new Date().toISOString(),
          sender: 'assistant',
          id: `ai_response_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          metadata: {
            nodeId: 'ai-agent_1753765465331',
            nodeType: 'ai-agent',
          },
        };

        // Add AI response message to chat
        addMessageToConversation(conversationId, aiMessage);
        console.log('✅ Added AI response message:', `${aiResponse.substring(0, 50)}...`);
      }

      // Then, create form message for the chat
      const formMessage = {
        type: messageType.form,
        content: {
          formTitle,
          formDescription,
          formFields,
          formNodeId,
          aiResponse: '', // Clear this since we're showing it separately
        },
        timestamp: new Date().toISOString(),
        sender: 'assistant',
      };

      // Add form message to chat
      addMessageToConversation(conversationId, formMessage);

      // Update active flow state - keep it active but waiting for form (pauses timer)
      // CRITICAL: Preserve executionId and flowId during form pause!
      setActiveFlowState((prev) => {
        const updatedState = {
          ...prev,
          isExecuting: false,
          isPaused: true,
          waitingForForm: true, // This pauses the timer
          currentFormNodeId: formNodeId,
          streamingConnection: null,
          progress: update.progress?.percentage || 0,
          currentNode: formNodeId,
          // Keep these values from the streaming execution:
          executionId: prev.executionId || update.execution_id, // Preserve execution ID!
          flowId: prev.flowId || update.execution_id, // Preserve flow ID!
          nodeStatuses: {
            ...prev.nodeStatuses,
            [formNodeId]: 'waiting_for_form',
          },
        };

        console.log('🔍 DEBUG - Updated activeFlowState for form pause:', {
          executionId: updatedState.executionId,
          flowId: updatedState.flowId,
          waitingForForm: updatedState.waitingForForm,
        });

        return updatedState;
      });

      console.log('✅ Form pause handled successfully with separate AI response');
    } else {
      console.error('❌ Invalid form data in streaming update:', update);
    }
  }, [addMessageToConversation, setActiveFlowState]);

  // Handle streaming flow errors with recovery option
  const handleStreamingFlowError = useCallback((update, conversationId) => {
    // Store error for recovery options
    setErrorRecoveryState((prev) => ({
      ...prev,
      error: update.error,
      executionId: update.execution_id,
      executionData: update.data || activeFlowState,
      lastError: update,
    }));

    const errorMessage = {
      type: messageType.answer,
      content: `❌ Flow execution failed: ${update.error}`,
      timestamp: new Date(),
      id: `flow_error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      metadata: {
        error: true,
        executionId: update.execution_id,
        canRecover: true,
        streaming: true,
        showErrorRecovery: true, // Flag to show inline recovery
      },
    };

    addMessageToConversation(conversationId, errorMessage);
    if (isMobile) setTimeout(() => setMobileForceRender((prev) => prev + 1), 100);

    // Reset flow state and stop timer on error
    setActiveFlowState((prev) => ({
      ...prev,
      isActive: false,
      isExecuting: false,
      waitingForForm: false,
      streamingConnection: null,
      startTime: null,
      elapsedSeconds: 0,
    }));

    setOnRequest(false);
    setLocalIsProcessing(false);
  }, [addMessageToConversation, isMobile, activeFlowState]);

  // NEW: Flow recovery handler
  const handleFlowRecovery = useCallback(async (executionId, conversationId) => {
    try {
      console.log('🔄 Attempting to recover execution:', executionId);
      toast.info('Attempting to recover flow execution...');

      const recoveryResult = await recoverFlowExecution(executionId);

      if (recoveryResult.success) {
        toast.success('Flow execution recovered successfully!');

        const recoveryMessage = {
          type: messageType.answer,
          content: '✅ Flow execution recovered and completed successfully.',
          timestamp: new Date(),
          id: `recovery_success_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          metadata: {
            recovered: true,
            previousAttempts: recoveryResult.recovery_info?.previous_attempts || 0,
          },
        };

        addMessageToConversation(conversationId, recoveryMessage);
        if (isMobile) setTimeout(() => setMobileForceRender((prev) => prev + 1), 100);
      } else {
        toast.error(`Recovery failed: ${recoveryResult.error}`);

        const recoveryFailMessage = {
          type: messageType.answer,
          content: `❌ Recovery failed: ${recoveryResult.error}`,
          timestamp: new Date(),
          id: `recovery_fail_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        };

        addMessageToConversation(conversationId, recoveryFailMessage);
        if (isMobile) setTimeout(() => setMobileForceRender((prev) => prev + 1), 100);
      }
    } catch (error) {
      console.error('Recovery error:', error);
      toast.error('Failed to recover flow execution');
    }
  }, [addMessageToConversation, isMobile]);

  // Enhanced error recovery handlers
  const handleErrorRecoverySuccess = useCallback((recoveryResult) => {
    toast.success('Flow recovered successfully!');

    // Clear error state
    setErrorRecoveryState((prev) => ({
      ...prev,
      showModal: false,
      error: null,
      executionId: null,
      executionData: null,
    }));

    // Add success message to conversation
    const successMessage = {
      type: messageType.answer,
      content: '✅ Flow execution recovered and resumed successfully.',
      timestamp: new Date(),
      id: `recovery_success_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      metadata: {
        recovery: true,
        streaming: true,
      },
    };

    addMessageToConversation(currentConversationId, successMessage);
    if (isMobile) setTimeout(() => setMobileForceRender((prev) => prev + 1), 100);
  }, [addMessageToConversation, isMobile, currentConversationId]);

  const handleRetryFromError = useCallback(() => {
    // Clear error state
    setErrorRecoveryState((prev) => ({
      ...prev,
      showModal: false,
      error: null,
    }));

    // Retry the last failed operation
    toast.info('Retrying flow execution...');

    // Add retry message
    const retryMessage = {
      type: messageType.answer,
      content: '🔄 Retrying flow execution from the beginning...',
      timestamp: new Date(),
      id: `retry_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      metadata: {
        retry: true,
        streaming: true,
      },
    };

    addMessageToConversation(currentConversationId, retryMessage);
    if (isMobile) setTimeout(() => setMobileForceRender((prev) => prev + 1), 100);

    // TODO: Implement retry logic based on last operation
  }, [addMessageToConversation, isMobile, currentConversationId]);

  const handleShowErrorDetails = useCallback(() => {
    setErrorRecoveryState((prev) => ({
      ...prev,
      showModal: true,
    }));
  }, []);

  // Monitoring functions
  const fetchPerformanceMetrics = useCallback(async () => {
    try {
      const langgraphUrl = getLangGraphBackendUrl();
      const response = await fetch(`${langgraphUrl}/debug/performance`);
      const data = await response.json();
      setPerformanceMetrics(data.performance_metrics || []);
    } catch (error) {
      console.error('Error fetching performance metrics:', error);
    }
  }, []);

  const fetchSystemStatus = useCallback(async () => {
    try {
      const langgraphUrl = getLangGraphBackendUrl();
      const response = await fetch(`${langgraphUrl}/debug/status`);
      const data = await response.json();
      setSystemStatus(data);
    } catch (error) {
      console.error('Error fetching system status:', error);
    }
  }, []);

  const fetchDebugData = useCallback(async () => {
    try {
      const langgraphUrl = getLangGraphBackendUrl();
      const [performanceResponse, statusResponse, errorsResponse] = await Promise.all([
        fetch(`${langgraphUrl}/debug/performance`),
        fetch(`${langgraphUrl}/debug/status`),
        fetch(`${langgraphUrl}/debug/errors`),
      ]);

      const performanceData = await performanceResponse.json();
      const statusData = await statusResponse.json();
      const errorsData = await errorsResponse.json();

      setDebugData({
        performance: performanceData.performance_metrics || [],
        status: statusData,
        errors: errorsData.error_log || [],
      });
    } catch (error) {
      console.error('Error fetching debug data:', error);
    }
  }, []);

  const handleOpenMonitoring = useCallback(async () => {
    setMonitoringDialogOpen(true);
    if (monitoringMode) {
      await fetchDebugData();
    }
  }, [monitoringMode, fetchDebugData]);

  // Performance optimization functions
  const applyPerformanceSettings = useCallback(async () => {
    try {
      const langgraphUrl = getLangGraphBackendUrl();
      const response = await fetch(`${langgraphUrl}/debug/performance/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          enableCaching,
          enableCompression,
          optimizeMemoryUsage,
          cacheTTL,
          maxConcurrentRequests,
        }),
      });

      if (response.ok) {
        toast.success('Performance settings applied successfully!');
      } else {
        toast.error('Failed to apply performance settings');
      }
    } catch (error) {
      console.error('Error applying performance settings:', error);
      toast.error('Failed to apply performance settings');
    }
  }, [enableCaching, enableCompression, optimizeMemoryUsage, cacheTTL, maxConcurrentRequests]);

  const clearCache = useCallback(async () => {
    try {
      const langgraphUrl = getLangGraphBackendUrl();
      const response = await fetch(`${langgraphUrl}/debug/cache/clear`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.ok) {
        toast.success('Cache cleared successfully!');
      } else {
        toast.error('Failed to clear cache');
      }
    } catch (error) {
      console.error('Error clearing cache:', error);
      toast.error('Failed to clear cache');
    }
  }, []);

  const getCacheStats = useCallback(async () => {
    try {
      const langgraphUrl = getLangGraphBackendUrl();
      const response = await fetch(`${langgraphUrl}/debug/cache/stats`);
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching cache stats:', error);
      return null;
    }
  }, []);

  // Fallback to traditional polling when streaming fails
  const fallbackToPolling = useCallback(async (flowData, input, conversationId) => {
    console.log('📡 Falling back to traditional polling...');

    try {
      // Use the traditional executeAgenticFlow with polling
      const result = await executeAgenticFlow(flowData.id || flowData._id, input);

      if (result.response) {
        const answerMessage = {
          type: messageType.answer,
          content: result.response.result,
          timestamp: new Date(),
          id: `fallback_complete_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          metadata: {
            fallback: true,
            executionId: result.response.executionId,
          },
        };

        addMessageToConversation(conversationId, answerMessage);
        if (isMobile) setTimeout(() => setMobileForceRender((prev) => prev + 1), 100);
      }
    } catch (error) {
      console.error('Fallback execution failed:', error);
      toast.error('Flow execution failed completely');
    } finally {
      setOnRequest(false);
      setLocalIsProcessing(false);
      setActiveFlowState((prev) => ({
        ...prev,
        isActive: false,
      }));
    }
  }, [addMessageToConversation, isMobile, setOnRequest]);

  // Legacy polling function - kept for backward compatibility
  const pollForFlowStatus = async (executionId, langgraphBackendUrl) => {
    console.warn('⚠️ Using legacy polling - consider upgrading to streaming');

    let pollCount = 0;
    const maxPolls = 60;
    const pollInterval = 2000;

    while (pollCount < maxPolls) {
      try {
        await new Promise((resolve) => setTimeout(resolve, pollInterval));
        pollCount++;

        const pollResponse = await fetch(getLangGraphFlowStatusUrl(executionId));
        const pollData = await pollResponse.json();

        if (pollData.status === 'completed' || pollData.status === 'error') {
          handleFormSubmissionResponse(pollData);
          break;
        } else if (pollData.status === 'paused' && pollData.waiting_for_form_input) {
          if (!activeFlowState.currentFormNodeId || activeFlowState.currentFormNodeId !== pollData.form_node_waiting) {
            handleFormSubmissionResponse(pollData);
            break;
          }
        }
      } catch (error) {
        console.error('Polling error:', error);
        if (pollCount >= 3) {
          toast.error('Error checking flow status. Please refresh the page.');
          break;
        }
      }
    }

    if (pollCount >= maxPolls) {
      toast.warning('Flow is taking longer than expected. Please check the status manually.');
    }
  };

  // Handle the response from form submission or polling
  const handleFormSubmissionResponse = (responseData) => {
    // Check if flow is completed
    const flowCompleted = responseData.status === 'completed'
                        || responseData.flow_completed
                        || responseData.result?.flow_completed
                        || responseData.result?.context?.flow_completed;

    if (flowCompleted) {
      const answerMessage = {
        type: messageType.answer,
        content: (responseData.result || responseData.result?.data || responseData.result?.result?.data || responseData.result?.result || 'Flow completed successfully'),
        timestamp: new Date(),
        id: `flow_complete_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      };
      addMessageToConversation(currentConversationId, answerMessage);
      if (isMobile) setTimeout(() => setMobileForceRender((prev) => prev + 1), 100);

      // Mark flow as completed
      endCurrentFlow();
      toast.success('Flow completed successfully!');
    } else {
      // Check if still waiting for form input - check top level first, then fallback to nested locations
      const stillWaitingForForm = responseData.waiting_for_form_input
                                || responseData.context?.waiting_for_form_input
                                || responseData.result?.waiting_for_form_input
                                || responseData.result?.state?.waiting_for_form_input;

      console.log('🔍 Form submission response analysis:', {
        status: responseData.status,
        waiting_for_form_input: responseData.waiting_for_form_input,
        form_node_waiting: responseData.form_node_waiting,
        form_fields: responseData.form_fields,
        form_node_id: responseData.form_node_id,
        stillWaitingForForm,
      });

      if (!stillWaitingForForm) {
        // Flow is still running but not waiting for form input
        const answerMessage = {
          type: messageType.answer,
          content: (responseData.result?.data || responseData.result?.result?.data || responseData.result?.result || responseData.result || 'Form processed successfully'),
          timestamp: new Date(),
          id: `form_response_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        };
        addMessageToConversation(currentConversationId, answerMessage);
        if (isMobile) setTimeout(() => setMobileForceRender((prev) => prev + 1), 100);

        setFlowExecutionId(null);

        // Update active flow state
        setActiveFlowState((prev) => ({
          ...prev,
          waitingForForm: false,
        }));
      } else {
        // Check for new form to display - prioritize top-level fields first
        const formFields = responseData.form_fields
                         || responseData.result?.state?.form_fields
                         || responseData.result?.state?.form_message?.content?.formFields
                         || responseData.form_message?.content?.formFields
                         || responseData.context?.form_data?.form_fields
                         || responseData.context?.form_data?.form_message?.content?.formFields;

        console.log('🔍 Form fields found:', formFields);

        if (formFields && formFields.length > 0) {
          console.log('🔍 DEBUG - Response data for form node ID extraction:', {
            form_node_waiting: responseData.form_node_waiting,
            form_node_id: responseData.form_node_id,
            result_state_form_node_id: responseData.result?.state?.form_node_id,
            result_state_form_message_id: responseData.result?.state?.form_message?.id,
            form_message_id: responseData.form_message?.id,
            context_form_data_form_node_id: responseData.context?.form_data?.form_node_id,
            fallback_id: `form_${Date.now()}`,
          });

          const formNodeId = responseData.form_node_waiting
                            || responseData.form_node_id
                            || responseData.result?.state?.form_node_id
                            || responseData.result?.state?.form_message?.id
                            || responseData.form_message?.id
                            || responseData.context?.form_data?.form_node_id
                            || `form_${Date.now()}`;

          console.log('🔍 Form node ID:', formNodeId);

          // Update active flow state with the form node ID
          console.log('🔍 DEBUG - Setting currentFormNodeId to:', formNodeId);
          setActiveFlowState((prev) => {
            console.log('🔍 DEBUG - Previous activeFlowState:', prev);
            const newState = {
              ...prev,
              waitingForForm: true,
              currentFormNodeId: formNodeId,
            };
            console.log('🔍 DEBUG - New activeFlowState:', newState);
            return newState;
          });
          const formMessage = {
            type: messageType.form,
            content: {
              title: responseData.form_title
                     || responseData.result?.state?.form_title
                     || responseData.result?.state?.form_message?.content?.title
                     || responseData.form_message?.content?.title
                     || responseData.context?.form_data?.form_title
                     || responseData.context?.form_data?.form_message?.content?.title
                     || 'Form',
              description: responseData.form_description
                          || responseData.result?.state?.form_description
                          || responseData.result?.state?.form_message?.content?.description
                          || responseData.form_message?.content?.description
                          || responseData.context?.form_data?.form_description
                          || responseData.context?.form_data?.form_message?.content?.description
                          || '',
              formFields,
              formId: formNodeId,
            },
            timestamp: new Date(),
            id: `form_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          };

          addMessageToConversation(currentConversationId, formMessage);
        } else {
          console.log('🔍 No form fields found in response');
        }
      }
    }
  };

  // Rich chat hook
  const {
    isProcessing,
    formData,
    activeForm,
    handleFormFieldChange,
    handleFormSubmit,
    handleSendRichMessage,
  } = useRichChat({
    username,
    selectedModel,
    selectedFlow,
    temperature,
    playAudio,
    onSendMessage: handleSendMessage,
    onFormSubmit: handleFormSubmission,
  });

  // Update local state when settings load from backend
  useEffect(() => {
    if (settings && !settingsLoading) {
      if (settings.chatSettings) {
        setSelectedModel(settings.chatSettings.selectedModel || 'llama4');
        setTemperature(settings.chatSettings.temperature || 0.7);
        setPlayAudio(settings.chatSettings.playAudio || false);
      }
    }
  }, [settings, settingsLoading]);

  // Listen for theme changes
  useEffect(() => {
    const handleThemeChange = (event) => {
      setDarkMode(event.detail.darkMode);
    };

    window.addEventListener('themeChanged', handleThemeChange);
    return () => window.removeEventListener('themeChanged', handleThemeChange);
  }, []);

  // Update local state when prop changes
  useEffect(() => {
    if (initialDarkMode !== undefined) {
      setDarkMode(initialDarkMode);
    }
  }, [initialDarkMode]);

  // Speech recognition setup
  useEffect(() => {
    if ('webkitSpeechRecognition' in window) {
      recognition.current = new window.webkitSpeechRecognition();
      recognition.current.continuous = true;
      recognition.current.interimResults = true;
      recognition.current.lang = 'en-US';

      recognition.current.onresult = (event) => {
        let finalTranscript = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          if (event.results[i].isFinal) {
            finalTranscript += event.results[i][0].transcript;
          }
        }
        if (finalTranscript) {
          setQuestion(finalTranscript);
        }
      };

      recognition.current.onerror = (error) => {
        console.error('Speech recognition error:', error);
        setIsListening(false);
      };
    }
  }, []);

  // Settings handlers
  const handleModelChange = async (newModel) => {
    setSelectedModel(newModel);
    await updateChatSettings({ selectedModel: newModel });
  };

  const handleTemperatureChange = async (newTemperature) => {
    setTemperature(newTemperature);
    await updateChatSettings({ temperature: newTemperature });
  };

  const handlePlayAudioChange = async (e) => {
    const newPlayAudio = e.target.checked;
    setPlayAudio(newPlayAudio);
    await updateChatSettings({ playAudio: newPlayAudio });
  };

  const handleToggleTheme = () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    localStorage.setItem('theme', newDarkMode ? 'dark' : 'light');
    window.dispatchEvent(new CustomEvent('themeChanged', {
      detail: { darkMode: newDarkMode },
    }));
  };

  // Widget handlers
  const handleToggleAudio = () => {
    setIsAudioEnabled(!isAudioEnabled);
    toast.info(`Audio ${!isAudioEnabled ? 'enabled' : 'disabled'}`);
  };

  const handleToggleMic = () => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  };

  const handleShowOptions = () => {
    setFormOptionsOpen(true);
  };

  const handleNewConversation = () => {
    createNewConversation();
    setFlowExecutionId(null);
    setFlowSessionId(null);
    setFlowContext({});

    // Reset MCP selections
    setSelectedMCPServer(null);
    setSelectedMCPTool(null);

    // Reset flow state when starting new conversation
    setActiveFlowState({
      isActive: false,
      flowId: null,
      executionId: null,
      sessionId: null,
      context: null,
      waitingForForm: false,
      completed: false,
    });
  };

  // Flow state management functions
  const startNewFlow = (flowId, executionId, sessionId, context) => {
    setActiveFlowState({
      isActive: true,
      flowId,
      executionId,
      sessionId,
      context,
      waitingForForm: false,
      completed: false,
    });
    setFlowExecutionId(executionId);
    setFlowSessionId(sessionId);
    setFlowContext(context);
    setFlowStartTime(Date.now()); // Set flow start time
  };

  const continueExistingFlow = (executionId, sessionId, context) => {
    setActiveFlowState((prev) => ({
      ...prev,
      executionId,
      sessionId,
      context,
      waitingForForm: false,
    }));
    setFlowExecutionId(executionId);
    setFlowSessionId(sessionId);
    setFlowContext(context);
    setFlowStartTime(Date.now()); // Set flow start time
  };

  const endCurrentFlow = () => {
    setActiveFlowState({
      isActive: false,
      flowId: null,
      executionId: null,
      sessionId: null,
      context: null,
      waitingForForm: false,
      completed: true,
      startTime: null,
      elapsedSeconds: 0,
    });
    setFlowExecutionId(null);
    setFlowSessionId(null);
    setFlowContext(null);
    setFlowStartTime(null); // Clear flow start time
  };

  const updateFlowState = (updates) => {
    setActiveFlowState((prev) => ({ ...prev, ...updates }));
  };

  const handleFileUpload = () => {
    // Trigger file upload
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '*/*';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        setFile(file);
        toast.success(`File "${file.name}" selected`);
      }
    };
    input.click();
  };

  const handleImageUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        setFile(file);
        toast.success(`Image "${file.name}" selected`);
      }
    };
    input.click();
  };

  const handleVoiceInput = () => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  };

  const handleEmojiPicker = () => {
    toast.info('Emoji picker coming soon!');
  };

  // Emoji picker functions
  const handleEmojiClick = (event) => {
    setShowEmojiPicker(true);
  };

  // Separate handler for speed dial emoji action
  const handleSpeedDialEmoji = () => {
    setShowEmojiPicker(true);
  };

  const handleEmojiClose = () => {
    setShowEmojiPicker(false);
  };

  const handleEmojiSelect = (emoji) => {
    setQuestion((prev) => prev + emoji);
    handleEmojiClose();
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: 'Chat Conversation',
        text: 'Check out this conversation!',
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard!');
    }
  };

  const handleExport = () => {
    const exportData = {
      conversationId: currentConversationId,
      messages: currentMessages,
      exportedAt: new Date().toISOString(),
      username,
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `chat-${currentConversationId || 'conversation'}-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Chat exported successfully!');
  };

  const handlePrint = () => {
    window.print();
  };

  const handleCopy = () => {
    const text = currentMessages.map((m) => `${m.type}: ${m.content}`).join('\n');
    navigator.clipboard.writeText(text);
    toast.success('Conversation copied to clipboard!');
  };

  const handleDelete = () => {
    if (currentConversationId) {
      deleteConversation(currentConversationId);
      toast.success('Conversation deleted!');
    }
  };

  const handleArchive = () => {
    if (currentConversationId) {
      archiveConversation(currentConversationId);
      toast.success('Conversation archived!');
    }
  };

  const handleFavorite = () => {
    if (currentConversationId) {
      toggleFavorite(currentConversationId);
      toast.success('Conversation favorited!');
    }
  };

  // Notification handlers
  const handleMarkAsRead = (id) => {
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
  };

  const handleMarkAllAsRead = () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    toast.success('All notifications marked as read');
  };

  const handleDeleteNotification = (id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  const handleClearAll = () => {
    setNotifications([]);
    toast.success('All notifications cleared');
  };

  // Speech functions
  const startListening = () => {
    if (recognition.current && !isListening) {
      recognition.current.start();
      setIsListening(true);
    }
  };

  const stopListening = () => {
    if (recognition.current && isListening) {
      recognition.current.stop();
      setIsListening(false);
    }
  };

  const speakResponse = (text) => {
    if (!window.speechSynthesis) return toast.error('Speech synthesis not supported.');
    if (recognition.current) recognition.current.stop();
    if (speech.current) window.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'en-US';
    utterance.rate = 0.9;
    utterance.pitch = 1;
    utterance.volume = 1;

    speech.current = utterance;

    utterance.onerror = (error) => console.error('Speech error:', error);

    window.speechSynthesis.speak(utterance);
  };

  const handleStopTyping = () => {
    if (typingInstance) typingInstance.stop();
    if (speech.current) window.speechSynthesis.cancel();
    if (window.speechSynthesis) window.speechSynthesis.cancel();
    stopListening();
    setTypingPaused(true);
    setOnRequest(false);
    setQuestion('');
  };

  // Main chat function
  const getAnswer = async () => {
    if (onRequest || !question.trim()) return;
    await getAnswerWithContent(question, currentConversationId);
  };

  // Main chat function with content parameter
  const getAnswerWithContent = async (content, providedConversationId) => {
    if (onRequest || !content.trim()) return;

    let conversationId = providedConversationId || currentConversationId;
    if (!conversationId) {
      const newConversation = await createNewConversation();
      conversationId = newConversation.id;
    }

    const questionMessage = {
      type: messageType.question,
      content, // Use content directly
      id: `q_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(),
    };

    addMessageToConversation(conversationId, questionMessage);
    if (isMobile) setMobileForceRender((prev) => prev + 1);

    const currentQuestion = content; // Use content directly
    setQuestion('');
    stopListening();
    setOnRequest(true);

    try {
      if (selectedMCPTool && selectedMCPServer) {
        // MCP tool execution logic
        console.log('Executing MCP tool:', selectedMCPTool.name, 'on server:', selectedMCPServer.name);

        const mcpResult = await mcpService.executeMCPToolFromChat(
          selectedMCPServer.id,
          selectedMCPTool.name,
          currentQuestion,
          conversationId,
        );

        const answerMessage = {
          type: messageType.answer,
          content: mcpResult.content,
          timestamp: new Date(),
          id: `mcp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          metadata: {
            tool: selectedMCPTool.name,
            server: selectedMCPServer.name,
            success: mcpResult.success,
          },
        };

        addMessageToConversation(conversationId, answerMessage);
        if (isMobile) setTimeout(() => setMobileForceRender((prev) => prev + 1), 100);
      } else if (selectedFlow) {
        // Flow execution logic - Updated to use Python backend with async polling

        // Check if we have an active flow and should continue it
        const flowId = selectedFlow._id || selectedFlow.id;
        const shouldContinueFlow = activeFlowState.isActive
                                 && activeFlowState.flowId === flowId
                                 && activeFlowState.executionId
                                 && !activeFlowState.completed;

        if (shouldContinueFlow) {
          // Continue existing flow instead of starting new one
          continueExistingFlow(activeFlowState.executionId, activeFlowState.sessionId, activeFlowState.context);
        } else {
          // Start new flow
          startNewFlow(flowId, null, null, null);
        }

        // First get the flow definition from Node.js backend
        const flowData = await axiosClient.get(`flows/${flowId}`);
        const flow = flowData.flow || flowData;

        // NEW: Use streaming execution instead of traditional polling
        console.log('🚀 Starting enhanced streaming execution...');

        const flowDataForStreaming = {
          id: flow.id || flow._id,
          name: flow.name,
          description: flow.description,
          nodes: flow.nodes,
          edges: flow.edges,
          globalState: flow.globalState,
          settings: flow.settings,
          userId: username,
        };

        const inputForStreaming = {
          input: currentQuestion,
          sessionId: shouldContinueFlow ? activeFlowState.sessionId : flowSessionId,
          context: shouldContinueFlow ? activeFlowState.context : flowContext,
          user_id: username,
        };

        // Start streaming execution
        const streamConnection = await streamFlowExecution(
          flowDataForStreaming,
          inputForStreaming,
          conversationId,
        );

        // Skip the old polling logic - streaming handles everything now
        return;
        // ALL CODE BELOW THIS RETURN WAS DEAD CODE - Removed for cleanup
        // eslint-disable-next-line no-unreachable
        if (initialResult?.waiting_for_form_input || initialResult?.state?.waiting_for_form_input) {
          const state = initialResult?.state || initialResult;
          const formFields = state?.form_fields || state?.form_message?.content?.formFields;

          if (state && formFields && formFields.length > 0) {
            const formNodeId = state.form_node_waiting || state.form_node_id || `form_${Date.now()}`;
            const formMessage = {
              type: messageType.form,
              content: {
                title: state.form_title || state.form_message?.content?.title || 'Form',
                description: state.form_description || state.form_message?.content?.description || '',
                formFields,
                formId: formNodeId,
              },
              timestamp: new Date(),
              id: `form_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            };

            addMessageToConversation(conversationId, formMessage);
            if (isMobile) setTimeout(() => setMobileForceRender((prev) => prev + 1), 100);
            return;
          }
        }

        // Poll for status
        let { status } = data;
        let pollResult = null;
        let pollError = null;
        let pollCount = 0;
        let currentExecutionId = executionId;

        // Start the flow execution timer
        const flowStartTime = Date.now();
        setActiveFlowState((prev) => ({
          ...prev,
          isActive: true,
          flowId: data.execution_id,
          executionId: data.execution_id,
          sessionId: data.sessionId,
          startTime: flowStartTime,
          elapsedSeconds: 0,
        }));

        while (status === 'pending' || status === 'running') {
          await new Promise((resolve) => setTimeout(resolve, 1000));
          pollCount++;

          const pollRes = await fetch(getLangGraphFlowStatusUrl(currentExecutionId));
          const pollData = await pollRes.json();
          status = pollData.status;
          pollResult = pollData;
          pollError = pollData.error;

          // Update execution ID if it changes
          if (pollData.execution_id && pollData.execution_id !== currentExecutionId) {
            currentExecutionId = pollData.execution_id;
            setFlowExecutionId(currentExecutionId);
          }

          // Check if flow is waiting for form input
          const isWaitingForForm = pollResult?.waiting_for_form_input;

          if (isWaitingForForm) {
            setFlowExecutionId(currentExecutionId);

            // Show form if available
            const formFields = pollResult?.form_fields;

            if (formFields && Array.isArray(formFields) && formFields.length > 0) {
              const formNodeId = pollResult.form_node_waiting || pollResult.form_node_id || `form_${Date.now()}`;

              // Update active flow state to show waiting for form
              setActiveFlowState((prev) => ({
                ...prev,
                waitingForForm: true,
                currentFormNodeId: formNodeId,
              }));
              const formMessage = {
                type: messageType.form,
                content: {
                  title: pollResult.form_title || 'Form',
                  description: pollResult.form_description || '',
                  formFields,
                  formId: formNodeId,
                },
                timestamp: new Date(),
                id: `form_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
              };

              addMessageToConversation(conversationId, formMessage);
              break; // Stop polling since we're waiting for form input
            }
          }

          // Timeout after 2 minutes
          if (pollCount > 120) {
            break;
          }
        }

        // Reset active flow state if flow is completed (but not if waiting for form input)
        const isWaitingForForm = pollResult?.waiting_for_form_input;

        if (status === 'complete' || status === 'success' || status === 'error') {
          setActiveFlowState((prev) => ({
            ...prev,
            isActive: false,
            completed: true,
            elapsedSeconds: 0,
          }));
        }
        // Don't reset if waiting for form input - keep the timer running

        const answerMessage = {
          type: messageType.answer,
          content: (pollResult?.data || pollResult?.result?.data || pollResult?.result || pollError || 'No response'),
          timestamp: new Date(),
          id: `a_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        };

        addMessageToConversation(conversationId, answerMessage);

        if (isMobile) setTimeout(() => setMobileForceRender((prev) => prev + 1), 100);
      } else {
        // Standard LLM chat
        const { response, err } = await chatCompletion({
          username,
          prompt: currentQuestion,
          model: selectedModel,
          conversationId,
          file,
        });

        if (response) {
          const answerMessage = {
            type: messageType.answer,
            content: response.text || response.message || 'No response',
            timestamp: new Date(),
            id: `a_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          };

          addMessageToConversation(conversationId, answerMessage);
          if (isMobile) setTimeout(() => setMobileForceRender((prev) => prev + 1), 100);
        }
      }
    } catch (error) {
      console.error('Request error:', error);
      const errorMessage = {
        type: messageType.answer,
        content: 'Sorry, there was an error processing your request.',
        timestamp: new Date(),
        id: `e_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      };

      addMessageToConversation(conversationId, errorMessage);
      if (isMobile) {
        setTimeout(() => setMobileForceRender((prev) => prev + 1), 50);
      }
    } finally {
      setOnRequest(false);
      setFile(null);
    }
  };

  // Timer cleanup is handled in the main timer useEffect

  // Get current user data
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%', // Changed from 100vh to 100%
      overflow: 'hidden',
      bgcolor: 'background.default',
    }}
    >
      {/* Enhanced Status Bar - Horizontal bar at top */}
      <Box sx={{ flexShrink: 0 }}>
        <ChatStatusBar
          isTyping={onRequest}
          isConnected={isConnected}
          isListening={isListening}
          isAudioEnabled={isAudioEnabled}
          currentUser={currentUser}
          onlineUsers={[]}
          onToggleAudio={handleToggleAudio}
          onToggleMic={handleToggleMic}
          onShowOptions={handleShowOptions}
        />
      </Box>

      {/* Main Content Area */}
      <Box sx={{
        display: 'flex',
        flex: 1,
        overflow: 'hidden',
        position: 'relative',
      }}
      >
        {/* Conversations Sidebar */}
        <Drawer
          variant={isMobile ? 'temporary' : 'persistent'}
          anchor="left"
          open={showConversations}
          onClose={() => setShowConversations(false)}
          sx={{
            '& .MuiDrawer-paper': {
              width: { xs: 280, sm: 320 },
              bgcolor: 'background.paper',
              borderRight: 1,
              borderColor: 'divider',
            },
          }}
        >
          <Box sx={{
            p: 2,
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: 'background.paper',
          }}
          >
            <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
              Conversations
            </Typography>
            <IconButton onClick={() => setShowConversations(false)} size="small">
              <ChevronLeftIcon />
            </IconButton>
          </Box>

          <ConversationList
            conversations={conversations}
            currentConversationId={currentConversationId}
            onSelectConversation={selectConversation}
            onDeleteConversation={deleteConversation}
            onRenameConversation={renameConversation}
            onToggleFavorite={toggleFavorite}
            onArchiveConversation={archiveConversation}
          />
        </Drawer>

        {/* Main Chat Area */}
        <Box
          key={isMobile ? mobileForceRender : 'desktop'}
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            overflow: 'hidden',
            ml: showConversations && !isMobile ? '320px' : 0,
            transition: theme && theme.transitions && typeof theme.transitions.create === 'function'
              ? theme.transitions.create('margin', {
                easing: theme.transitions.easing?.sharp || undefined,
                duration: theme.transitions.duration?.enteringScreen || undefined,
              })
              : undefined,
            position: 'relative',
          }}
        >
          {/* Messages Area - Use RichChatInterface for form support */}
          <Box sx={{
            flex: 1,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
          >
            <RichChatInterface
              messages={currentMessages}
              onSendMessage={handleSendMessage}
              onFormSubmit={handleFormSubmission}
              onFileUpload={handleFileUpload}
              onVoiceInput={handleVoiceInput}
              onEmojiClick={handleEmojiClick}
              isProcessing={onRequest || localIsProcessing}
              selectedModel={selectedModel}
              temperature={temperature}
              playAudio={playAudio}
              darkMode={darkMode}
              messagesEndRef={messagesEndRef}
              inputRef={inputRef}
              question={question}
              setQuestion={setQuestion}
              file={file}
              setFile={setFile}
              isListening={isListening}
              startListening={startListening}
              stopListening={stopListening}
              sessionStartTime={sessionStartTime}
              flowState={activeFlowState}
              onEndFlow={endCurrentFlow}
            />
          </Box>
        </Box>
      </Box>

      {/* Flow Status Indicator */}
      {activeFlowState.isActive && (
        <Box
          sx={{
            position: 'fixed',
            bottom: {
              xs: 200, // Below Chat Quick Actions
              sm: 200, // Below Chat Quick Actions
            },
            right: {
              xs: 16, // Standard margin on mobile
              sm: 20, // Standard margin on desktop
            },
            zIndex: 1000,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 3,
            p: 2,
            minWidth: 300,
            border: 1,
            borderColor: 'primary.main',
          }}
        >
          <Box sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1,
          }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main' }}>
              🔄 Active Flow
            </Typography>
            <IconButton
              size="small"
              onClick={endCurrentFlow}
              sx={{ color: 'error.main' }}
              title="End Flow"
            >
              <StopIcon />
            </IconButton>
          </Box>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
            Flow ID:
            {' '}
            {activeFlowState.flowId}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
            Status:
            {' '}
            {activeFlowState.waitingForForm ? '⏸️ Waiting for form' : '🔄 Running'}
          </Typography>
          {activeFlowState.isActive && (
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1, fontFamily: 'monospace' }}>
              Time:
              {' '}
              {activeFlowState.elapsedSeconds}
              s
              {' '}
              {activeFlowState.waitingForForm && '(⏸️ Paused)'}
              {/* Debug: {JSON.stringify({ isActive: activeFlowState.isActive, elapsedSeconds: activeFlowState.elapsedSeconds })} */}
            </Typography>
          )}
          {activeFlowState.executionId && (
            <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'monospace' }}>
              Exec:
              {' '}
              {activeFlowState.executionId.substring(0, 8)}
              ...
            </Typography>
          )}
        </Box>
      )}

      {/* Enhanced Quick Actions */}
      <ChatQuickActions
        onNewConversation={handleNewConversation}
        onFileUpload={handleFileUpload}
        onImageUpload={handleImageUpload}
        onVoiceInput={handleVoiceInput}
        onEmojiPicker={handleSpeedDialEmoji}
        onShare={handleShare}
        onExport={handleExport}
        onPrint={handlePrint}
        onCopy={handleCopy}
        onDelete={handleDelete}
        onArchive={handleArchive}
        onFavorite={handleFavorite}
        isFavorite={conversations.find((c) => c.id === currentConversationId)?.isFavorite || false}
        hasAttachments={!!file}
        canShare
      />

      {/* Emoji Picker Modal */}
      {showEmojiPicker && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(0, 0, 0, 0.6)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            animation: 'fadeIn 0.3s ease-in-out',
            '@keyframes fadeIn': {
              '0%': { opacity: 0 },
              '100%': { opacity: 1 },
            },
          }}
          onClick={handleEmojiClose}
        >
          <Paper
            sx={{
              p: 3,
              maxWidth: 600,
              maxHeight: 500,
              overflow: 'auto',
              position: 'relative',
              borderRadius: 3,
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              bgcolor: 'background.paper',
              color: 'text.primary',
              animation: 'slideIn 0.3s ease-out',
              '@keyframes slideIn': {
                '0%': {
                  opacity: 0,
                  transform: 'scale(0.8) translateY(-20px)',
                },
                '100%': {
                  opacity: 1,
                  transform: 'scale(1) translateY(0)',
                },
              },
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 3,
              pb: 2,
              borderBottom: 1,
              borderColor: 'divider',
            }}
            >
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                😊 Emoji Picker
              </Typography>
              <IconButton
                onClick={handleEmojiClose}
                size="small"
                sx={{
                  color: 'text.secondary',
                  '&:hover': {
                    bgcolor: 'action.hover',
                    transform: 'scale(1.1)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <ClearIcon />
              </IconButton>
            </Box>

            <Typography
              variant="body1"
              sx={{
                mb: 3,
                textAlign: 'center',
                color: 'text.secondary',
                fontStyle: 'italic',
              }}
            >
              ✨ Click an emoji to add it to your message ✨
            </Typography>

            <Grid container spacing={1.5}>
              {commonEmojis.map((emoji, index) => (
                <Grid item key={index}>
                  <IconButton
                    onClick={() => handleEmojiSelect(emoji)}
                    sx={{
                      fontSize: '1.8rem',
                      p: 1.5,
                      bgcolor: 'background.default',
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 2,
                      '&:hover': {
                        bgcolor: 'action.hover',
                        transform: 'scale(1.2) rotate(5deg)',
                        boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                        borderColor: 'primary.main',
                      },
                      '&:active': {
                        transform: 'scale(0.95)',
                      },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      animation: `emojiAppear 0.5s ease-out ${index * 0.02}s both`,
                      '@keyframes emojiAppear': {
                        '0%': {
                          opacity: 0,
                          transform: 'scale(0) rotate(-180deg)',
                        },
                        '100%': {
                          opacity: 1,
                          transform: 'scale(1) rotate(0deg)',
                        },
                      },
                    }}
                  >
                    {emoji}
                  </IconButton>
                </Grid>
              ))}
            </Grid>

            {/* Footer with categories */}
            <Box sx={{
              mt: 3,
              pt: 2,
              borderTop: 1,
              borderColor: 'divider',
              textAlign: 'center',
            }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontStyle: 'italic',
                }}
              >
                🎨 200+ emojis available • Click outside to close
              </Typography>
            </Box>
          </Paper>
        </Box>
      )}

      {/* Floating Action Buttons */}
      <Box sx={{
        position: 'fixed',
        top: 120,
        right: 24,
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
      >
        <Tooltip title="Conversation History" placement="left">
          <IconButton
            onClick={() => setShowConversations(!showConversations)}
            sx={{
              bgcolor: showConversations ? 'secondary.main' : 'background.paper',
              color: showConversations ? 'secondary.contrastText' : 'text.primary',
              border: 1,
              borderColor: showConversations ? 'secondary.main' : 'divider',
              boxShadow: 2,
              '&:hover': {
                bgcolor: showConversations ? 'secondary.dark' : 'action.hover',
                boxShadow: 4,
              },
              width: 44,
              height: 44,
            }}
          >
            <HistoryIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Chat Insights" placement="left">
          <IconButton
            onClick={() => setShowSidePanel(!showSidePanel)}
            sx={{
              bgcolor: showSidePanel ? 'info.main' : 'background.paper',
              color: showSidePanel ? 'info.contrastText' : 'text.primary',
              border: 1,
              borderColor: showSidePanel ? 'info.main' : 'divider',
              boxShadow: 2,
              '&:hover': {
                bgcolor: showSidePanel ? 'info.dark' : 'action.hover',
                boxShadow: 4,
              },
              width: 44,
              height: 44,
            }}
          >
            <Insights fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Notifications" placement="left">
          <IconButton
            onClick={() => setShowNotifications(!showNotifications)}
            sx={{
              bgcolor: 'background.paper',
              color: 'text.primary',
              border: 1,
              borderColor: 'divider',
              boxShadow: 2,
              '&:hover': {
                bgcolor: 'action.hover',
                boxShadow: 4,
              },
              width: 44,
              height: 44,
            }}
          >
            <Badge badgeContent={notifications.filter((n) => !n.read).length} color="error">
              <Notifications fontSize="small" />
            </Badge>
          </IconButton>
        </Tooltip>

        <Tooltip title="Monitoring Dashboard" placement="left">
          <IconButton
            onClick={handleOpenMonitoring}
            sx={{
              bgcolor: monitoringMode ? 'primary.main' : 'background.paper',
              color: monitoringMode ? 'primary.contrastText' : 'text.primary',
              border: 1,
              borderColor: 'divider',
              boxShadow: 2,
              '&:hover': {
                bgcolor: monitoringMode ? 'primary.dark' : 'action.hover',
                boxShadow: 4,
              },
              width: 44,
              height: 44,
            }}
          >
            <SettingsIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Enhanced Side Panel */}
      <ChatSidePanel
        open={showSidePanel}
        onClose={() => setShowSidePanel(false)}
        currentConversation={conversations.find((c) => c.id === currentConversationId)}
        conversations={conversations}
        analytics={analytics}
        insights={insights}
        onSelectConversation={selectConversation}
        onDeleteConversation={deleteConversation}
        onArchiveConversation={archiveConversation}
        onFavoriteConversation={toggleFavorite}
      />

      {/* Notification Center */}
      <ChatNotificationCenter
        open={showNotifications}
        onClose={() => setShowNotifications(false)}
        notifications={notifications}
        onMarkAsRead={handleMarkAsRead}
        onMarkAllAsRead={handleMarkAllAsRead}
        onDeleteNotification={handleDeleteNotification}
        onClearAll={handleClearAll}
      />

      {/* Form Options Drawer */}
      <FormOptionsDrawer
        open={formOptionsOpen}
        onClose={() => setFormOptionsOpen(false)}
        selectedModel={selectedModel}
        setSelectedModel={handleModelChange}
        temperature={temperature}
        setTemperature={handleTemperatureChange}
        playAudio={playAudio}
        handlePlayAudioChange={handlePlayAudioChange}
        darkMode={darkMode}
        onToggleTheme={handleToggleTheme}
        selectedFlow={selectedFlow}
        setSelectedFlow={setSelectedFlow}
        selectedMCPServer={selectedMCPServer}
        setSelectedMCPServer={setSelectedMCPServer}
        selectedMCPTool={selectedMCPTool}
        setSelectedMCPTool={setSelectedMCPTool}
      />

      {/* Error Recovery Modal */}
      <ErrorRecoveryModal
        open={errorRecoveryState.showModal}
        onClose={() => setErrorRecoveryState((prev) => ({ ...prev, showModal: false }))}
        executionId={errorRecoveryState.executionId}
        error={errorRecoveryState.error}
        executionData={errorRecoveryState.executionData}
        onRecoverySuccess={handleErrorRecoverySuccess}
        onRetry={handleRetryFromError}
      />

      {/* Monitoring Dashboard Dialog */}
      <Dialog open={monitoringDialogOpen} onClose={() => setMonitoringDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">Chat Monitoring Dashboard</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <FormControlLabel
                control={(
                  <Switch
                    checked={monitoringMode}
                    onChange={(e) => {
                      setMonitoringMode(e.target.checked);
                      if (e.target.checked) {
                        fetchDebugData();
                      }
                    }}
                    color="primary"
                  />
                )}
                label="Enable Monitoring"
              />
              <FormControlLabel
                control={(
                  <Switch
                    checked={performanceMode}
                    onChange={(e) => setPerformanceMode(e.target.checked)}
                    color="secondary"
                  />
                )}
                label="Performance Mode"
              />
              <Button
                onClick={fetchDebugData}
                disabled={!monitoringMode}
                size="small"
                startIcon={<SettingsIcon />}
              >
                Refresh
              </Button>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {!monitoringMode ? (
            <Alert severity="info" sx={{ mb: 3 }}>
              Enable monitoring to view chat performance, system health, and execution metrics.
            </Alert>
          ) : (
            <Box sx={{ mt: 2 }}>
              {/* System Status */}
              {debugData?.status && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    System Status
                  </Typography>
                  <Paper sx={{ p: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Status:
                          {' '}
                          <Chip label={debugData.status.status || 'Unknown'} color="success" size="small" />
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Uptime:
                          {' '}
                          {debugData.status.uptime || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Active Executions:
                          {' '}
                          {debugData.status.active_executions || 0}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Total Executions:
                          {' '}
                          {debugData.status.total_executions || 0}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Box>
              )}

              {/* Performance Metrics */}
              {debugData?.performance && debugData.performance.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Performance Metrics
                  </Typography>
                  <Grid container spacing={2}>
                    {debugData.performance.map((metric, index) => (
                      <Grid item xs={12} md={6} key={index}>
                        <Card>
                          <CardContent>
                            <Typography variant="subtitle2" gutterBottom>
                              {metric.name || `Metric ${index + 1}`}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Value:
                              {' '}
                              {metric.value || 'N/A'}
                            </Typography>
                            {metric.description && (
                              <Typography variant="caption" color="text.secondary">
                                {metric.description}
                              </Typography>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {/* Error Log */}
              {debugData?.errors && debugData.errors.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Recent Errors
                  </Typography>
                  <Paper sx={{ maxHeight: 300, overflow: 'auto', p: 2 }}>
                    {debugData.errors.slice(-10).map((error, index) => (
                      <Alert key={index} severity="error" sx={{ mb: 1 }}>
                        <Typography variant="body2">
                          <strong>
                            {error.timestamp}
                            :
                          </strong>
                          {' '}
                          {error.message}
                        </Typography>
                        {error.details && (
                          <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                            Details:
                            {' '}
                            {error.details}
                          </Typography>
                        )}
                      </Alert>
                    ))}
                  </Paper>
                </Box>
              )}

              {debugData && (
                <Alert severity="success">
                  Monitoring data refreshed successfully at
                  {' '}
                  {new Date().toLocaleTimeString()}
                </Alert>
              )}

              {/* Performance Optimization Section */}
              {performanceMode && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Performance Optimization
                  </Typography>
                  <Paper sx={{ p: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <FormControlLabel
                          control={(
                            <Switch
                              checked={enableCaching}
                              onChange={(e) => setEnableCaching(e.target.checked)}
                              color="primary"
                            />
                          )}
                          label="Enable Caching"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormControlLabel
                          control={(
                            <Switch
                              checked={enableCompression}
                              onChange={(e) => setEnableCompression(e.target.checked)}
                              color="primary"
                            />
                          )}
                          label="Enable Compression"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormControlLabel
                          control={(
                            <Switch
                              checked={optimizeMemoryUsage}
                              onChange={(e) => setOptimizeMemoryUsage(e.target.checked)}
                              color="primary"
                            />
                          )}
                          label="Optimize Memory Usage"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Cache TTL (seconds):
                          {' '}
                          {cacheTTL}
                        </Typography>
                        <input
                          type="range"
                          min="300"
                          max="7200"
                          step="300"
                          value={cacheTTL}
                          onChange={(e) => setCacheTTL(parseInt(e.target.value))}
                          style={{ width: '100%' }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Max Concurrent Requests:
                          {' '}
                          {maxConcurrentRequests}
                        </Typography>
                        <input
                          type="range"
                          min="1"
                          max="20"
                          step="1"
                          value={maxConcurrentRequests}
                          onChange={(e) => setMaxConcurrentRequests(parseInt(e.target.value))}
                          style={{ width: '100%' }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                          <Button
                            onClick={applyPerformanceSettings}
                            variant="contained"
                            color="primary"
                            size="small"
                          >
                            Apply Settings
                          </Button>
                          <Button
                            onClick={clearCache}
                            variant="outlined"
                            color="warning"
                            size="small"
                          >
                            Clear Cache
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMonitoringDialogOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default HomePageEnhanced;
